import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';
import { toast } from 'react-toastify';
import { formatImageUrl, stripHtml } from '../../utils/security';
import { useAuth } from '../../context/AuthContext';

// Media Assets
import ai1 from '../../images/portfolio/baraka-ai.jpg';
import enterpriseAutomationImg from '../../images/blog/enterprise-automation.jpg';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

// Smart image resolver matching blog slugs to high-impact corporate visuals
const resolveBlogImage = (slug, rawImage) => {
    if (slug === 'enterprise-automation') {
        return enterpriseAutomationImg;
    }
    if (rawImage) {
        const formatted = formatImageUrl(rawImage);
        if (formatted && !formatted.includes('ai1') && !formatted.includes('ai2') && !formatted.includes('ai3')) {
            return formatted;
        }
    }
    if (slug === 'ai-revolution-2026') {
        return ai1;
    }
    return enterpriseAutomationImg || ai1;
};

const BlogDetails = () => {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const { lang: urlLang, id: slugOrId } = useParams();
    const lang = urlLang || i18n.language || 'en';
    
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [readProgress, setReadProgress] = useState(0);
    const [copied, setCopied] = useState(false);
    const [claps, setClaps] = useState(24);
    const [hasClapped, setHasClapped] = useState(false);

    // Scroll & language initialization
    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
        window.scrollTo(0, 0);
    }, [lang, slugOrId, i18n]);

    // Reading progress listener
    useEffect(() => {
        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            if (totalScroll > 0) {
                const currentProgress = (window.scrollY / totalScroll) * 100;
                setReadProgress(Math.min(100, Math.max(0, currentProgress)));
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Fetch blog post
    const fetchBlog = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}blogs.php?slug=${slugOrId}&lang=${lang}`);
            const data = await response.json();
            
            if (data && !data.error) {
                const activeSlug = data.slug || slugOrId;
                const finalImage = resolveBlogImage(activeSlug, data.image);
                
                setBlog({
                    ...data,
                    slug: activeSlug,
                    image: finalImage
                });

                // Log view if user is logged in
                if (user) {
                    fetch(`${API_URL}users.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'log_view',
                            user_id: user.id,
                            entity_type: 'blog',
                            entity_id: data.id
                        })
                    }).catch(err => console.error('Logging error:', err));
                }
            } else {
                // Fallback from static translations if DB doesn't have it
                const blogItems = t('blog.items', { returnObjects: true });
                if (blogItems && blogItems[slugOrId]) {
                    const item = blogItems[slugOrId];
                    setBlog({
                        id: slugOrId,
                        slug: slugOrId,
                        title: item.title,
                        summary: item.summary,
                        content: item.content,
                        date: item.date,
                        author: item.author || 'Orbitronix Tech Team',
                        category: 'tech',
                        image: resolveBlogImage(slugOrId, null)
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching blog from DB:', error);
            // Fallback from static translations
            const blogItems = t('blog.items', { returnObjects: true });
            if (blogItems && blogItems[slugOrId]) {
                const item = blogItems[slugOrId];
                setBlog({
                    id: slugOrId,
                    slug: slugOrId,
                    title: item.title,
                    summary: item.summary,
                    content: item.content,
                    date: item.date,
                    author: item.author || 'Orbitronix Tech Team',
                    category: 'tech',
                    image: resolveBlogImage(slugOrId, null)
                });
            }
        }
        setLoading(false);
    }, [slugOrId, lang, user, t]);

    useEffect(() => {
        fetchBlog();
    }, [fetchBlog]);

    if (!blog && !loading) {
        return <Navigate to={`/${lang}/blog`} replace />;
    }

    if (loading) {
        return (
            <Layout>
                <div className="blog-loading-screen">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="mt-3 text-white-50">Loading Research Intelligence...</span>
                </div>
            </Layout>
        );
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        toast.success(lang === 'ka' ? 'ბმული დაკოპირდა!' : 'Link copied to clipboard!');
        setTimeout(() => setCopied(false), 3000);
    };

    const handleClap = () => {
        if (!hasClapped) {
            setClaps(prev => prev + 1);
            setHasClapped(true);
            toast.info(lang === 'ka' ? 'მადლობა შეფასებისთვის! 👏' : 'Thank you for your feedback! 👏');
        }
    };

    const authorRole = lang === 'ka' 
        ? 'სისტემური არქიტექტურა & AI პრაქტიკა' 
        : 'Enterprise Systems & AI Solutions Group';

    const categoryTitle = blog.category 
        ? (blog.category.toUpperCase() === 'TECH' ? (lang === 'ka' ? 'ტექნოლოგიები & AI' : 'Enterprise AI & Tech') : blog.category.toUpperCase())
        : (lang === 'ka' ? 'ინოვაციები' : 'Enterprise Technology');

    return (
        <Fragment>
            <Seo 
                title={blog.seo_title || `${blog.title} | Orbitronix Insights`}
                description={blog.seo_description || stripHtml(blog.summary)}
                keywords={blog.seo_keywords || 'enterprise automation, large language models, AI, ERP, CRM'}
                url={`/${lang}/blog/${slugOrId}`}
            />

            {/* Reading progress bar */}
            <div 
                className="blog-reading-progress" 
                style={{ width: `${readProgress}%` }} 
                role="progressbar"
                aria-valuenow={readProgress}
                aria-valuemin="0"
                aria-valuemax="100"
            />

            <Layout>
                <main className="page_content blog-detail-wrapper">
                    
                    {/* Atmospheric Glow Backdrop */}
                    <div className="blog-ambient-glow" />

                    {/* Corporate Hero Header */}
                    <header className="blog-corp-header pt-160 pb-40">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-xl-10 col-lg-11">
                                    <motion.div
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                    >
                                        {/* Breadcrumbs Navigation */}
                                        <nav className="blog-breadcrumb-nav mb-4" aria-label="breadcrumb">
                                            <Link to={`/${lang}`} className="b-crumb">
                                                <i className="fas fa-home me-1" />
                                                {lang === 'ka' ? 'მთავარი' : 'Home'}
                                            </Link>
                                            <span className="b-sep">/</span>
                                            <Link to={`/${lang}/blog`} className="b-crumb">
                                                {lang === 'ka' ? 'ბლოგი & ანალიტიკა' : 'Insights & Intelligence'}
                                            </Link>
                                            <span className="b-sep">/</span>
                                            <span className="b-current">{categoryTitle}</span>
                                        </nav>

                                        {/* Category Badge & Report Meta */}
                                        <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
                                            <span className="blog-corp-pill">
                                                <span className="corp-pill-dot" />
                                                {categoryTitle}
                                            </span>
                                            <span className="blog-intel-tag">
                                                <i className="fas fa-microchip me-1" />
                                                ORBX WHITE PAPER • 2026
                                            </span>
                                        </div>

                                        {/* Executive Headline */}
                                        <h1 className="blog-corp-title mb-4">
                                            {blog.title}
                                        </h1>

                                        {/* Executive Metadata Ribbon */}
                                        <div className="blog-meta-ribbon">
                                            <div className="meta-author-group">
                                                <div className="author-avatar-badge">
                                                    <span className="avatar-icon">⚡</span>
                                                </div>
                                                <div className="author-info">
                                                    <span className="author-name">
                                                        {blog.author || 'Orbitronix Tech Team'}
                                                        <i className="fas fa-check-circle text-primary ms-1" title="Verified Author" />
                                                    </span>
                                                    <span className="author-role">{authorRole}</span>
                                                </div>
                                            </div>

                                            <div className="meta-stats-group">
                                                <div className="meta-stat-item">
                                                    <span className="stat-label">{lang === 'ka' ? 'გამოქვეყნდა' : 'Published'}</span>
                                                    <span className="stat-val">
                                                        <i className="far fa-calendar-alt me-1 text-primary" />
                                                        {blog.date}
                                                    </span>
                                                </div>
                                                <div className="meta-stat-item">
                                                    <span className="stat-label">{lang === 'ka' ? 'წაკითხვის დრო' : 'Reading Time'}</span>
                                                    <span className="stat-val">
                                                        <i className="far fa-clock me-1 text-primary" />
                                                        {t('blog.minRead', { count: 5 })}
                                                    </span>
                                                </div>
                                                <div className="meta-stat-item d-none d-md-flex">
                                                    <span className="stat-label">{lang === 'ka' ? 'აუდიტორია' : 'Target Sector'}</span>
                                                    <span className="stat-val text-info">
                                                        <i className="fas fa-shield-alt me-1" />
                                                        Enterprise & Gov
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Header Quick Share */}
                                            <div className="meta-share-quick">
                                                <button 
                                                    className="quick-share-btn" 
                                                    onClick={copyToClipboard}
                                                    title={lang === 'ka' ? 'ბმულის კოპირება' : 'Copy Link'}
                                                >
                                                    <i className={`fas ${copied ? 'fa-check text-success' : 'fa-link'}`} />
                                                    <span>{copied ? (lang === 'ka' ? 'დაკოპირდა' : 'Copied') : (lang === 'ka' ? 'გაზიარება' : 'Share')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Dynamic Featured Post Image Showcase */}
                    <section className="blog-featured-media-section pb-50">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-xl-10 col-lg-11">
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.98, y: 30 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.7, delay: 0.2 }}
                                        className="blog-media-card"
                                    >
                                        {/* Corporate Featured Image Frame */}
                                        <div 
                                            className="media-image-wrapper"
                                            onClick={() => setLightboxOpen(true)}
                                            title={lang === 'ka' ? 'დააწკაპუნეთ სრულ ეკრანზე სანახავად' : 'Click to inspect full high-resolution diagram'}
                                        >
                                            <img 
                                                src={blog.image} 
                                                alt={blog.title}
                                                className="blog-hero-image"
                                                loading="eager"
                                            />
                                            
                                            {/* Specular glare sweep overlay */}
                                            <div className="media-glare-overlay" />

                                            {/* Top Overlay Badges */}
                                            <div className="media-overlay-top">
                                                <div className="media-chip media-chip-live">
                                                    <span className="pulse-live-indicator" />
                                                    <span>LIVE ARCHITECTURE BLUEPRINT</span>
                                                </div>
                                                <div className="media-chip media-chip-tag">
                                                    <span>LLM + ERP/CRM SYNC</span>
                                                </div>
                                            </div>

                                            {/* Bottom Telemetry Bar */}
                                            <div className="media-overlay-bottom">
                                                <div className="media-caption-info">
                                                    <i className="fas fa-project-diagram me-2 text-info" />
                                                    <span>
                                                        {lang === 'ka' 
                                                            ? 'სქემა 1.1: მრავალ-აგენტიანი LLM ორკესტრაცია და მონაცემთა ნაკადები' 
                                                            : 'Figure 1.1: Multi-Agent LLM Orchestration & Database Integration Architecture'}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="media-expand-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setLightboxOpen(true);
                                                    }}
                                                    title={lang === 'ka' ? 'სრული ეკრანი' : 'Full Screen Preview'}
                                                >
                                                    <i className="fas fa-expand-alt me-1" />
                                                    <span className="d-none d-sm-inline">{lang === 'ka' ? 'გადიდება' : 'Expand'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Corporate 2-Column Article Content & Sidebar */}
                    <section className="blog-article-content-section pb-100">
                        <div className="container">
                            <div className="row justify-content-center g-5">
                                
                                {/* Left: Main Corporate Content Body (8 cols) */}
                                <div className="col-xl-7 col-lg-8">
                                    <motion.article 
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.7, delay: 0.3 }}
                                        className="blog-corp-article"
                                    >
                                        {/* Executive Summary Lead Block */}
                                        <div className="executive-summary-box mb-45">
                                            <div className="exec-lead-header">
                                                <i className="fas fa-quote-left exec-quote-icon" />
                                                <span className="exec-lead-tag">
                                                    {lang === 'ka' ? 'მმართველი რეზიუმე' : 'EXECUTIVE SUMMARY'}
                                                </span>
                                            </div>
                                            <p className="exec-lead-text">
                                                {stripHtml(blog.summary)}
                                            </p>
                                        </div>

                                        {/* Corporate Strategic Takeaways Briefing */}
                                        <div className="corp-briefing-card mb-50">
                                            <div className="briefing-header">
                                                <i className="fas fa-layer-group text-primary me-2" />
                                                <h3 className="briefing-title">
                                                    {lang === 'ka' ? 'საკვანძო კორპორატიული დასკვნები' : 'Key Strategic Architecture Takeaways'}
                                                </h3>
                                            </div>
                                            <div className="briefing-grid">
                                                <div className="briefing-item">
                                                    <div className="briefing-icon">01</div>
                                                    <div className="briefing-content">
                                                        <h4>{lang === 'ka' ? 'ბუნებრივი ენით მონაცემთა წვდომა' : 'Conversational Data Extraction'}</h4>
                                                        <p>{lang === 'ka' 
                                                            ? 'თანამშრომლებს შეუძლიათ მონაცემთა ბაზებს დაუსვან კითხვები ბუნებრივი ენით და მიიღონ მყისიერი ანალიტიკა.' 
                                                            : 'Teams query cross-departmental ERP and CRM databases using natural language, eliminating SQL reporting bottlenecks.'}</p>
                                                    </div>
                                                </div>
                                                <div className="briefing-item">
                                                    <div className="briefing-icon">02</div>
                                                    <div className="briefing-content">
                                                        <h4>{lang === 'ka' ? 'ავტონომიური პროცესების მართვა' : 'Autonomous Action Execution'}</h4>
                                                        <p>{lang === 'ka' 
                                                            ? 'ავტონომიური აგენტები ასრულებენ მრავალსაფეხურიან ამოცანებს, რაც ამცირებს საოპერაციო დანახარჯებს 40%-ით.' 
                                                            : 'Multi-agent workflows trigger cross-system transactions automatically with full role-based audit logging.'}</p>
                                                    </div>
                                                </div>
                                                <div className="briefing-item">
                                                    <div className="briefing-icon">03</div>
                                                    <div className="briefing-content">
                                                        <h4>{lang === 'ka' ? 'სრული მონაცემთა სუვერენიტეტი' : 'Private & Self-Hosted Security'}</h4>
                                                        <p>{lang === 'ka' 
                                                            ? 'კერძო, დაცული LLM ინფრასტრუქტურა გარანტიას იძლევა GDPR და მუნიციპალური უსაფრთხოების სტანდარტების დაცვის.' 
                                                            : 'On-premise or dedicated sovereign cloud deployment ensures strict compliance with ISO 27001 and data confidentiality.'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Main Formatted Article Content */}
                                        <div 
                                            className="blog-prose-content" 
                                            dangerouslySetInnerHTML={{ __html: blog.content }} 
                                        />

                                        {/* Deep Dive Architecture Callout */}
                                        <div className="corp-deepdive-box mt-5 mb-5">
                                            <div className="d-flex align-items-start gap-3">
                                                <div className="deepdive-badge">
                                                    <i className="fas fa-lightbulb" />
                                                </div>
                                                <div>
                                                    <h4 className="deepdive-heading">
                                                        {lang === 'ka' ? 'ინტეგრაციის საუკეთესო პრაქტიკა' : 'Implementation Insight for CTOs & CIOs'}
                                                    </h4>
                                                    <p className="deepdive-desc">
                                                        {lang === 'ka'
                                                            ? 'ERP/CRM სისტემებში LLM-ის ინტეგრაციისას გადამწყვეტია Role-Based Access Control (RBAC) შრის შენარჩუნება, რათა აგენტებმა მხოლოდ მომხმარებლის შესაბამისი უფლებების ფარგლებში შეძლონ მონაცემთა დამუშავება.'
                                                            : 'When deploying LLM interfaces across legacy SAP, Oracle Fusion, or custom CRM solutions, enforcing fine-grained Row-Level Security at the semantic layer is non-negotiable to prevent unauthorized data exposure.'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Topic Tags */}
                                        <div className="blog-tags-row mt-45 mb-40">
                                            <span className="tags-label">
                                                <i className="fas fa-tags me-1 text-primary" />
                                                {lang === 'ka' ? 'თემები:' : 'Topics:'}
                                            </span>
                                            <div className="tags-list">
                                                <span className="tag-chip">#EnterpriseAutomation</span>
                                                <span className="tag-chip">#LargeLanguageModels</span>
                                                <span className="tag-chip">#CRMIntegration</span>
                                                <span className="tag-chip">#DigitalTransformation</span>
                                            </div>
                                        </div>

                                        {/* Article Engagement & Feedback Bar */}
                                        <div className="article-feedback-bar mb-60">
                                            <div className="d-flex align-items-center gap-3">
                                                <button 
                                                    className={`feedback-clap-btn ${hasClapped ? 'active' : ''}`}
                                                    onClick={handleClap}
                                                >
                                                    <span className="clap-emoji">👏</span>
                                                    <span className="clap-count">{claps}</span>
                                                    <span className="clap-text">{lang === 'ka' ? 'მოწონება' : 'Insightful'}</span>
                                                </button>
                                            </div>

                                            <div className="feedback-share-links">
                                                <span className="share-lead">{lang === 'ka' ? 'გაუზიარეთ გუნდს:' : 'Share Intelligence:'}</span>
                                                <a 
                                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="share-social-icon in"
                                                    title="Share on LinkedIn"
                                                >
                                                    <i className="fab fa-linkedin-in" />
                                                </a>
                                                <a 
                                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="share-social-icon tw"
                                                    title="Share on X"
                                                >
                                                    <i className="fab fa-twitter" />
                                                </a>
                                                <a 
                                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="share-social-icon fb"
                                                    title="Share on Facebook"
                                                >
                                                    <i className="fab fa-facebook-f" />
                                                </a>
                                                <button 
                                                    onClick={copyToClipboard}
                                                    className="share-social-icon copy"
                                                    title="Copy Article Link"
                                                >
                                                    <i className="fas fa-link" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Previous & Next Article Navigation Cards */}
                                        <div className="corp-post-navigation">
                                            <div className="row g-3">
                                                <div className="col-sm-6">
                                                    {blog.prev_slug ? (
                                                        <Link to={`/${lang}/blog/${blog.prev_slug}`} className="nav-card-corp prev">
                                                            <div className="nav-meta-dir">
                                                                <i className="fas fa-arrow-left me-1" />
                                                                {lang === 'ka' ? 'წინა სტატია' : 'PREVIOUS BRIEFING'}
                                                            </div>
                                                            <div className="nav-title-text">{blog.prev_title || blog.prev_slug}</div>
                                                        </Link>
                                                    ) : (
                                                        <Link to={`/${lang}/blog`} className="nav-card-corp prev">
                                                            <div className="nav-meta-dir">
                                                                <i className="fas fa-th-large me-1" />
                                                                {lang === 'ka' ? 'ბლოგის სია' : 'ALL INSIGHTS'}
                                                            </div>
                                                            <div className="nav-title-text">{lang === 'ka' ? 'სტატიების კატალოგი' : 'Browse All Articles'}</div>
                                                        </Link>
                                                    )}
                                                </div>

                                                <div className="col-sm-6 text-sm-end">
                                                    {blog.next_slug ? (
                                                        <Link to={`/${lang}/blog/${blog.next_slug}`} className="nav-card-corp next">
                                                            <div className="nav-meta-dir">
                                                                {lang === 'ka' ? 'შემდეგი სტატია' : 'NEXT BRIEFING'}
                                                                <i className="fas fa-arrow-right ms-1" />
                                                            </div>
                                                            <div className="nav-title-text">{blog.next_title || blog.next_slug}</div>
                                                        </Link>
                                                    ) : (
                                                        <Link to={`/${lang}/contact`} className="nav-card-corp next">
                                                            <div className="nav-meta-dir">
                                                                {lang === 'ka' ? 'კონსულტაცია' : 'NEXT STEP'}
                                                                <i className="fas fa-arrow-right ms-1" />
                                                            </div>
                                                            <div className="nav-title-text">{lang === 'ka' ? 'დაგვიკავშირდით' : 'Connect with Engineers'}</div>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.article>
                                </div>

                                {/* Right: Sticky Corporate Sidebar (4 cols) */}
                                <div className="col-xl-4 col-lg-4">
                                    <aside className="blog-corp-sidebar">
                                        
                                        {/* Author Desk Card */}
                                        <div className="sidebar-widget-card author-desk-card mb-4">
                                            <div className="widget-header-title">
                                                <i className="fas fa-id-badge text-primary me-2" />
                                                <span>{lang === 'ka' ? 'კვლევითი ჯგუფი' : 'RESEARCH DESK'}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-3 my-3">
                                                <div className="author-corp-avatar">
                                                    <span>ORBX</span>
                                                </div>
                                                <div>
                                                    <h5 className="author-corp-name mb-1">Orbitronix Engineering</h5>
                                                    <span className="author-corp-badge">Enterprise AI Practice</span>
                                                </div>
                                            </div>
                                            <p className="author-corp-bio">
                                                {lang === 'ka'
                                                    ? 'Orbitronix-ის საინჟინრო გუნდი ქმნის მასშტაბირებად, უსაფრთხო და მუნიციპალურ სტანდარტებთან თავსებად ციფრულ ეკოსისტემებს.'
                                                    : 'Specialized in deploying sovereign AI workflows, resilient APIs, and enterprise data orchestration architectures.'}
                                            </p>
                                        </div>

                                        {/* Executive CTA: Schedule Discovery Session */}
                                        <div className="sidebar-widget-card corp-cta-widget mb-4">
                                            <div className="cta-icon-pill">
                                                <i className="fas fa-robot" />
                                            </div>
                                            <h4 className="cta-widget-title">
                                                {lang === 'ka' ? 'გსურთ LLM ინტეგრაცია თქვენს ბიზნესში?' : 'Automate Your Enterprise Workflows'}
                                            </h4>
                                            <p className="cta-widget-desc">
                                                {lang === 'ka'
                                                    ? 'დაჯავშნეთ 30-წუთიანი უფასო არქიტექტურული კონსულტაცია ჩვენს წამყვან ინჟინრებთან.'
                                                    : 'Consult directly with our solution architects to plan private LLM deployment for your ERP & CRM systems.'}
                                            </p>
                                            <Link to={`/${lang}/contact`} className="cta-widget-btn">
                                                <span>{lang === 'ka' ? 'კონსულტაციის მოთხოვნა' : 'Schedule Discovery Call'}</span>
                                                <i className="fas fa-arrow-right" />
                                            </Link>
                                        </div>

                                        {/* Architecture Specs Widget */}
                                        <div className="sidebar-widget-card specs-widget mb-4">
                                            <div className="widget-header-title">
                                                <i className="fas fa-sliders-h text-primary me-2" />
                                                <span>{lang === 'ka' ? 'ტექნოლოგიური პარამეტრები' : 'SYSTEM SPECIFICATIONS'}</span>
                                            </div>
                                            <ul className="specs-list">
                                                <li className="spec-item">
                                                    <span className="spec-key">Deployment:</span>
                                                    <span className="spec-value text-success">Sovereign / On-Prem</span>
                                                </li>
                                                <li className="spec-item">
                                                    <span className="spec-key">Model Types:</span>
                                                    <span className="spec-value">Custom Fine-Tuned LLMs</span>
                                                </li>
                                                <li className="spec-item">
                                                    <span className="spec-key">Supported DBs:</span>
                                                    <span className="spec-value">PostgreSQL, Oracle, MySQL</span>
                                                </li>
                                                <li className="spec-item">
                                                    <span className="spec-key">Security Standard:</span>
                                                    <span className="spec-value text-info">ISO 27001 & RBAC</span>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Related Articles Widget */}
                                        <div className="sidebar-widget-card related-widget">
                                            <div className="widget-header-title">
                                                <i className="fas fa-newspaper text-primary me-2" />
                                                <span>{lang === 'ka' ? 'დაკავშირებული ანალიტიკა' : 'RELATED RESEARCH'}</span>
                                            </div>
                                            <div className="related-articles-list mt-3">
                                                <Link to={`/${lang}/blog/ai-revolution-2026`} className="related-item">
                                                    <span className="related-meta">MAY 10, 2026 • AI REPORT</span>
                                                    <h6 className="related-title">The AI Revolution: What to Expect in 2026</h6>
                                                </Link>
                                                <Link to={`/${lang}/blog/future-of-coding`} className="related-item">
                                                    <span className="related-meta">APRIL 15, 2026 • DEV INTELLIGENCE</span>
                                                    <h6 className="related-title">The Future of Software Development in the AI Era</h6>
                                                </Link>
                                            </div>
                                        </div>

                                    </aside>
                                </div>
                            </div>
                        </div>
                    </section>

                </main>
            </Layout>

            {/* High-Resolution Interactive Lightbox Modal */}
            <AnimatePresence>
                {lightboxOpen && (
                    <motion.div 
                        className="blog-lightbox-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setLightboxOpen(false)}
                    >
                        <div className="lightbox-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="lightbox-header">
                                <div className="lightbox-title-wrap">
                                    <i className="fas fa-image me-2 text-primary" />
                                    <span>{blog.title}</span>
                                </div>
                                <button 
                                    className="lightbox-close-btn"
                                    onClick={() => setLightboxOpen(false)}
                                    aria-label="Close"
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </div>
                            <div className="lightbox-image-container">
                                <img 
                                    src={blog.image} 
                                    alt={blog.title} 
                                    className="lightbox-img" 
                                />
                            </div>
                            <div className="lightbox-footer">
                                <span className="lightbox-caption">
                                    {lang === 'ka' 
                                        ? 'Orbitronix Enterprise Architecture • დიდი ენობრივი მოდელების ინტეგრაცია CRM და ERP სისტემებთან' 
                                        : 'Orbitronix Enterprise Architecture • Large Language Models Integration with CRM & ERP Systems'}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`
                /* Reading Progress Bar */
                .blog-reading-progress {
                    position: fixed;
                    top: 0;
                    left: 0;
                    height: 3.5px;
                    background: linear-gradient(90deg, #6c5ce7 0%, #38bdf8 50%, #34d399 100%);
                    box-shadow: 0 0 12px rgba(56, 189, 248, 0.8);
                    z-index: 9999;
                    transition: width 0.1s ease-out;
                }

                .blog-detail-wrapper {
                    background: #060918;
                    color: #e2e8f0;
                    position: relative;
                    min-height: 100vh;
                }

                /* Ambient Glow */
                .blog-ambient-glow {
                    position: absolute;
                    top: 80px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 900px;
                    height: 450px;
                    background: radial-gradient(ellipse at center, rgba(108, 92, 231, 0.16) 0%, rgba(56, 189, 248, 0.08) 40%, transparent 70%);
                    filter: blur(80px);
                    pointer-events: none;
                    z-index: 0;
                }

                /* Loading Screen */
                .blog-loading-screen {
                    height: 80vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #060918;
                }

                /* Breadcrumbs */
                .blog-breadcrumb-nav {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 6px 16px;
                    border-radius: 30px;
                }
                .b-crumb {
                    color: rgba(255, 255, 255, 0.6);
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .b-crumb:hover {
                    color: #fff;
                }
                .b-sep {
                    color: rgba(255, 255, 255, 0.25);
                }
                .b-current {
                    color: #38bdf8;
                    font-weight: 600;
                }

                /* Badges */
                .blog-corp-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(108, 92, 231, 0.16);
                    border: 1px solid rgba(108, 92, 231, 0.45);
                    color: #a5b4fc;
                    padding: 5px 14px;
                    border-radius: 20px;
                    font-size: 11.5px;
                    font-weight: 700;
                    letter-spacing: 1px;
                }
                .corp-pill-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #38bdf8;
                    box-shadow: 0 0 8px #38bdf8;
                }
                .blog-intel-tag {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    color: rgba(255, 255, 255, 0.5);
                }

                /* Title */
                .blog-corp-title {
                    font-size: clamp(2.1rem, 4vw, 3.4rem);
                    font-weight: 800;
                    line-height: 1.22;
                    color: #ffffff;
                    letter-spacing: -0.02em;
                    background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 65%, #cbd5e1 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                /* Metadata Ribbon */
                .blog-meta-ribbon {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 20px;
                    padding: 18px 24px;
                    background: rgba(14, 20, 48, 0.65);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 18px;
                    backdrop-filter: blur(12px);
                }
                .meta-author-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .author-avatar-badge {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #6c5ce7, #38bdf8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.35);
                }
                .author-info {
                    display: flex;
                    flex-direction: column;
                }
                .author-name {
                    font-weight: 700;
                    font-size: 15px;
                    color: #ffffff;
                }
                .author-role {
                    font-size: 11.5px;
                    color: rgba(255, 255, 255, 0.5);
                }

                .meta-stats-group {
                    display: flex;
                    align-items: center;
                    gap: 24px;
                }
                .meta-stat-item {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .stat-label {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    color: rgba(255, 255, 255, 0.4);
                }
                .stat-val {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.85);
                }

                .quick-share-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #fff;
                    font-size: 12.5px;
                    font-weight: 600;
                    padding: 8px 18px;
                    border-radius: 30px;
                    cursor: pointer;
                    transition: all 0.25s ease;
                }
                .quick-share-btn:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                    transform: translateY(-2px);
                }

                /* Featured Media Showcase */
                .blog-media-card {
                    position: relative;
                    z-index: 2;
                }
                .media-image-wrapper {
                    position: relative;
                    width: 100%;
                    max-height: 520px;
                    aspect-ratio: 16 / 9;
                    border-radius: 24px;
                    overflow: hidden;
                    background: #090e24;
                    border: 1px solid rgba(255, 255, 255, 0.16);
                    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(108, 92, 231, 0.2);
                    cursor: pointer;
                    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease;
                }
                .media-image-wrapper:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 35px 75px rgba(0, 0, 0, 0.8), 0 0 55px rgba(56, 189, 248, 0.3);
                }
                .blog-hero-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .media-image-wrapper:hover .blog-hero-image {
                    transform: scale(1.025);
                }
                .media-glare-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        135deg,
                        rgba(255, 255, 255, 0.15) 0%,
                        transparent 40%,
                        transparent 70%,
                        rgba(56, 189, 248, 0.08) 100%
                    );
                    pointer-events: none;
                }

                .media-overlay-top {
                    position: absolute;
                    top: 16px;
                    left: 20px;
                    right: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    z-index: 3;
                }
                .media-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 10.5px;
                    font-weight: 800;
                    letter-spacing: 0.8px;
                    backdrop-filter: blur(8px);
                }
                .media-chip-live {
                    background: rgba(14, 20, 48, 0.85);
                    border: 1px solid rgba(52, 211, 153, 0.4);
                    color: #34d399;
                }
                .pulse-live-indicator {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #34d399;
                    box-shadow: 0 0 8px #34d399;
                    animation: pulse-live 1.8s infinite;
                }
                .media-chip-tag {
                    background: rgba(14, 20, 48, 0.85);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    color: #fff;
                }

                .media-overlay-bottom {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    padding: 14px 22px;
                    background: linear-gradient(to top, rgba(7, 10, 26, 0.95) 0%, rgba(7, 10, 26, 0.7) 60%, transparent 100%);
                    backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                    z-index: 3;
                }
                .media-caption-info {
                    font-size: 12.5px;
                    font-weight: 500;
                    color: rgba(255, 255, 255, 0.9);
                    display: flex;
                    align-items: center;
                }
                .media-expand-btn {
                    background: rgba(255, 255, 255, 0.12);
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    color: #fff;
                    font-size: 11.5px;
                    font-weight: 700;
                    padding: 5px 12px;
                    border-radius: 20px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .media-expand-btn:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                }

                /* Executive Lead Box */
                .executive-summary-box {
                    background: rgba(14, 20, 52, 0.65);
                    border-left: 4px solid #38bdf8;
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                    border-right: 1px solid rgba(255, 255, 255, 0.08);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 0 18px 18px 0;
                    padding: 26px 30px;
                    position: relative;
                }
                .exec-lead-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                }
                .exec-quote-icon {
                    color: #38bdf8;
                    font-size: 16px;
                }
                .exec-lead-tag {
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                    color: #38bdf8;
                }
                .exec-lead-text {
                    font-size: 1.18rem;
                    line-height: 1.7;
                    color: #ffffff;
                    font-weight: 500;
                    margin: 0;
                }

                /* Corporate Strategic Takeaways */
                .corp-briefing-card {
                    background: rgba(12, 17, 44, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: 28px 26px;
                }
                .briefing-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 22px;
                    padding-bottom: 14px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }
                .briefing-title {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #fff;
                    margin: 0;
                }
                .briefing-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                }
                .briefing-item {
                    display: flex;
                    gap: 16px;
                }
                .briefing-icon {
                    flex-shrink: 0;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    background: rgba(108, 92, 231, 0.2);
                    border: 1px solid rgba(108, 92, 231, 0.4);
                    color: #a5b4fc;
                    font-size: 12px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .briefing-content h4 {
                    font-size: 15px;
                    font-weight: 700;
                    color: #fff;
                    margin: 0 0 4px;
                }
                .briefing-content p {
                    font-size: 13.5px;
                    line-height: 1.6;
                    color: rgba(255, 255, 255, 0.65);
                    margin: 0;
                }

                /* Article Prose Content */
                .blog-prose-content {
                    font-size: 17.5px;
                    line-height: 1.88;
                    color: rgba(255, 255, 255, 0.85);
                }
                .blog-prose-content p {
                    margin-bottom: 24px;
                }
                .blog-prose-content h2, 
                .blog-prose-content h3 {
                    color: #fff;
                    font-weight: 800;
                    margin-top: 40px;
                    margin-bottom: 18px;
                    line-height: 1.35;
                }
                .blog-prose-content ul, 
                .blog-prose-content ol {
                    margin-bottom: 24px;
                    padding-left: 24px;
                }
                .blog-prose-content li {
                    margin-bottom: 8px;
                    color: rgba(255, 255, 255, 0.8);
                }

                /* Deep Dive Box */
                .corp-deepdive-box {
                    background: rgba(56, 189, 248, 0.05);
                    border: 1px solid rgba(56, 189, 248, 0.25);
                    border-radius: 16px;
                    padding: 22px 24px;
                }
                .deepdive-badge {
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    background: rgba(56, 189, 248, 0.15);
                    color: #38bdf8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    flex-shrink: 0;
                }
                .deepdive-heading {
                    font-size: 15px;
                    font-weight: 800;
                    color: #fff;
                    margin: 0 0 6px;
                }
                .deepdive-desc {
                    font-size: 13px;
                    line-height: 1.65;
                    color: rgba(255, 255, 255, 0.7);
                    margin: 0;
                }

                /* Tags */
                .blog-tags-row {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                    padding-top: 20px;
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                }
                .tags-label {
                    font-size: 12px;
                    font-weight: 700;
                    color: rgba(255, 255, 255, 0.5);
                }
                .tags-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .tag-chip {
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 20px;
                    background: rgba(255, 255, 255, 0.05);
                    color: #a5b4fc;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }

                /* Feedback & Social */
                .article-feedback-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 18px;
                    padding: 18px 24px;
                    background: rgba(14, 20, 48, 0.6);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 18px;
                }
                .feedback-clap-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #fff;
                    padding: 8px 16px;
                    border-radius: 30px;
                    cursor: pointer;
                    transition: all 0.25s;
                }
                .feedback-clap-btn:hover {
                    background: rgba(108, 92, 231, 0.25);
                    border-color: #6c5ce7;
                    transform: scale(1.05);
                }
                .feedback-clap-btn.active {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                }
                .clap-emoji { font-size: 18px; }
                .clap-count { font-weight: 800; font-size: 14px; }
                .clap-text { font-size: 12px; opacity: 0.8; }

                .feedback-share-links {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .share-lead {
                    font-size: 12px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.5);
                }
                .share-social-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.06);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                    text-decoration: none;
                    transition: all 0.2s;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }
                .share-social-icon:hover {
                    transform: translateY(-2px);
                    color: #fff;
                }
                .share-social-icon.in:hover { background: #0a66c2; }
                .share-social-icon.tw:hover { background: #1da1f2; }
                .share-social-icon.fb:hover { background: #1877f2; }
                .share-social-icon.copy:hover { background: #6c5ce7; }

                /* Post Navigation Cards */
                .corp-post-navigation {
                    margin-top: 40px;
                }
                .nav-card-corp {
                    display: block;
                    padding: 20px;
                    background: rgba(14, 20, 48, 0.65);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 16px;
                    text-decoration: none;
                    transition: all 0.3s;
                    height: 100%;
                }
                .nav-card-corp:hover {
                    background: rgba(19, 27, 66, 0.85);
                    border-color: rgba(108, 92, 231, 0.4);
                    transform: translateY(-3px);
                }
                .nav-meta-dir {
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1px;
                    color: #a5b4fc;
                    margin-bottom: 6px;
                }
                .nav-title-text {
                    font-size: 14.5px;
                    font-weight: 700;
                    color: #fff;
                    line-height: 1.4;
                }

                /* Corporate Sidebar Widgets */
                .blog-corp-sidebar {
                    position: sticky;
                    top: 100px;
                }
                .sidebar-widget-card {
                    background: rgba(14, 20, 48, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 24px;
                    backdrop-filter: blur(12px);
                }
                .widget-header-title {
                    font-size: 11.5px;
                    font-weight: 800;
                    letter-spacing: 1.4px;
                    color: rgba(255, 255, 255, 0.75);
                    display: flex;
                    align-items: center;
                    padding-bottom: 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }

                .author-corp-avatar {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #fff;
                    font-weight: 800;
                    font-size: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    letter-spacing: 1px;
                }
                .author-corp-name {
                    font-size: 14.5px;
                    font-weight: 700;
                    color: #fff;
                }
                .author-corp-badge {
                    font-size: 11px;
                    color: #38bdf8;
                }
                .author-corp-bio {
                    font-size: 12.5px;
                    line-height: 1.6;
                    color: rgba(255, 255, 255, 0.6);
                    margin: 0;
                }

                /* CTA Widget */
                .corp-cta-widget {
                    background: linear-gradient(145deg, rgba(108, 92, 231, 0.25) 0%, rgba(14, 20, 48, 0.8) 100%);
                    border: 1px solid rgba(108, 92, 231, 0.45);
                    text-align: center;
                    padding: 30px 24px;
                }
                .cta-icon-pill {
                    width: 50px;
                    height: 50px;
                    border-radius: 14px;
                    background: #6c5ce7;
                    color: #fff;
                    font-size: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                    box-shadow: 0 8px 20px rgba(108, 92, 231, 0.45);
                }
                .cta-widget-title {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #fff;
                    line-height: 1.35;
                    margin-bottom: 10px;
                }
                .cta-widget-desc {
                    font-size: 13px;
                    line-height: 1.6;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 20px;
                }
                .cta-widget-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    width: 100%;
                    padding: 12px 20px;
                    border-radius: 30px;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #fff;
                    font-weight: 700;
                    font-size: 13px;
                    text-decoration: none;
                    transition: all 0.3s;
                    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.35);
                }
                .cta-widget-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(108, 92, 231, 0.5);
                    color: #fff;
                }

                /* Specs Widget */
                .specs-list {
                    list-style: none;
                    padding: 0;
                    margin: 14px 0 0;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .spec-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-size: 12.5px;
                    padding-bottom: 8px;
                    border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
                }
                .spec-key {
                    color: rgba(255, 255, 255, 0.5);
                }
                .spec-value {
                    font-weight: 600;
                    color: #fff;
                }

                /* Related Widget */
                .related-articles-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .related-item {
                    display: block;
                    text-decoration: none;
                    padding-bottom: 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                    transition: transform 0.2s;
                }
                .related-item:hover {
                    transform: translateX(4px);
                }
                .related-meta {
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    color: #38bdf8;
                    display: block;
                    margin-bottom: 4px;
                }
                .related-title {
                    font-size: 13.5px;
                    font-weight: 700;
                    color: #fff;
                    line-height: 1.4;
                    margin: 0;
                    transition: color 0.2s;
                }
                .related-item:hover .related-title {
                    color: #a5b4fc;
                }

                /* Lightbox Modal */
                .blog-lightbox-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(3, 6, 18, 0.92);
                    backdrop-filter: blur(14px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                }
                .lightbox-dialog {
                    max-width: 1080px;
                    width: 100%;
                    background: #090d22;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.8), 0 0 50px rgba(108, 92, 231, 0.3);
                }
                .lightbox-header {
                    padding: 16px 20px;
                    background: rgba(14, 20, 48, 0.9);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .lightbox-title-wrap {
                    font-size: 14px;
                    font-weight: 700;
                    color: #fff;
                }
                .lightbox-close-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .lightbox-close-btn:hover {
                    background: #dc2626;
                }
                .lightbox-image-container {
                    padding: 12px;
                    background: #040714;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .lightbox-img {
                    max-width: 100%;
                    max-height: 75vh;
                    object-fit: contain;
                    border-radius: 12px;
                }
                .lightbox-footer {
                    padding: 14px 20px;
                    background: rgba(14, 20, 48, 0.9);
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                    font-size: 12.5px;
                    color: rgba(255, 255, 255, 0.7);
                    text-align: center;
                }

                @keyframes pulse-live {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(0.85); }
                }

                @media (max-width: 991px) {
                    .blog-corp-sidebar {
                        position: static;
                        margin-top: 40px;
                    }
                    .media-image-wrapper {
                        aspect-ratio: 16 / 10;
                    }
                }
                @media (max-width: 576px) {
                    .blog-meta-ribbon {
                        padding: 16px;
                    }
                    .meta-stats-group {
                        gap: 14px;
                    }
                    .executive-summary-box {
                        padding: 20px;
                    }
                }
            `}</style>
        </Fragment>
    );
};

export default BlogDetails;
